import React, { useEffect, useRef } from "react";
import { createChart, CrosshairMode } from "lightweight-charts";
import { TrendingUp, Target, ShieldAlert, BarChart3, Info } from "lucide-react";

const ETFTradeCard = ({ etf }) => {
  const candleChartRef = useRef(null);
  const rsChartRef = useRef(null);

  const fmt = (v, dec = 2) => v != null ? v.toFixed(dec) : '—';
  const fmtPct = (v, dec = 1) => v != null ? `${v >= 0 ? '+' : ''}${v.toFixed(dec)}%` : '—';

  // Metrics for Targets/Stops
  const r = etf.atr || etf.price * 0.02;
  const target2R = etf.price + r * 2;
  const target3R = etf.price + r * 3;
  const stop1R = etf.price - r;
  const stop2R = etf.price - r * 2;

  useEffect(() => {
    if (!candleChartRef.current || !etf.candles) return;

    const chart = createChart(candleChartRef.current, {
      autoSize: true,
      layout: {
        background: { color: 'transparent' },
        textColor: '#a1a1aa',
        fontSize: 10,
        fontFamily: 'Inter, sans-serif',
      },
      grid: {
        vertLines: { color: 'rgba(255, 255, 255, 0.03)' },
        horzLines: { color: 'rgba(255, 255, 255, 0.03)' },
      },
      crosshair: {
        mode: CrosshairMode.Normal,
      },
      rightPriceScale: { borderColor: 'rgba(255, 255, 255, 0.1)', visible: true },
      timeScale: { borderColor: 'rgba(255, 255, 255, 0.1)', visible: true },
    });

    const candleSeries = chart.addCandlestickSeries({
      upColor: '#10b981',
      downColor: '#ef4444',
      borderUpColor: '#10b981',
      borderDownColor: '#ef4444',
      wickUpColor: '#10b981',
      wickDownColor: '#ef4444',
    });

    candleSeries.setData(etf.candles);

    if (etf.ema10_line) {
      const ema10 = chart.addLineSeries({ color: '#06b6d4', lineWidth: 1, priceLineVisible: false });
      ema10.setData(etf.ema10_line);
    }
    if (etf.ema21_line) {
      const ema21 = chart.addLineSeries({ color: '#16a34a', lineWidth: 1, priceLineVisible: false });
      ema21.setData(etf.ema21_line);
    }
    if (etf.sma50_line) {
      const sma50 = chart.addLineSeries({ color: '#dc2626', lineWidth: 1.5, priceLineVisible: false });
      sma50.setData(etf.sma50_line);
    }

    chart.timeScale().fitContent();

    // RS Chart
    let rsChart;
    if (rsChartRef.current && etf.rs_line && etf.rs_line.length > 0) {
      rsChart = createChart(rsChartRef.current, {
        autoSize: true,
        layout: {
          background: { color: 'transparent' },
          textColor: '#71717a',
          fontSize: 9,
        },
        grid: {
            vertLines: { visible: false },
            horzLines: { color: 'rgba(255, 255, 255, 0.03)' },
        },
        rightPriceScale: { borderColor: 'rgba(255, 255, 255, 0.1)' },
        timeScale: { visible: false },
        handleScroll: false,
        handleScale: false,
      });

      const rsArea = rsChart.addAreaSeries({
        lineColor: etf.rs_trend === 'up' ? '#10b981' : '#f43f5e',
        topColor: etf.rs_trend === 'up' ? 'rgba(16, 185, 129, 0.2)' : 'rgba(244, 63, 94, 0.2)',
        bottomColor: 'rgba(0, 0, 0, 0)',
        lineWidth: 2,
        priceLineVisible: false,
      });
      rsArea.setData(etf.rs_line);
      rsChart.timeScale().fitContent();
    }

    return () => {
      chart.remove();
      if (rsChart) rsChart.remove();
    };
  }, [etf]);


  const trendClass = (status) => {
    if (status?.includes('Above All')) return 'bg-emerald-500/10 text-emerald-500 border-emerald-500/20';
    if (status?.includes('Below All')) return 'bg-rose-500/10 text-rose-500 border-rose-500/20';
    return 'bg-zinc-500/10 text-zinc-400 border-zinc-500/20';
  };

  return (
    <div className="bg-[#09090b] p-6 rounded-b-xl border-t border-zinc-800 shadow-[0_-20px_50px_-20px_rgba(0,0,0,0.5)] animate-in slide-in-from-top-4 duration-500">
      <div className="flex flex-col gap-6">
        {/* Row 1: The Main Chart Context */}
        <div className="bg-black/40 border border-zinc-800/50 rounded-xl overflow-hidden shadow-2xl relative">
          <div className="absolute top-3 left-4 z-10 flex items-center gap-3">
             <span className="text-base font-black text-white tracking-widest">{etf.ticker} — {etf.country} (3M)</span>
          </div>
          <div className="absolute top-3 right-4 z-10">
             <span className={`text-[12px] px-3 py-1 rounded-full border font-black uppercase tracking-widest shadow-lg ${trendClass(etf.trend_status)}`}>
               {etf.trend_status}
             </span>
          </div>

          <div className="flex flex-col">
            <div className="h-[340px]" ref={candleChartRef} />
            <div className="h-[90px] border-t border-zinc-800/30 relative bg-zinc-950/10" ref={rsChartRef}>
               <div className="absolute top-2 left-4 flex flex-col">
                 <span className="text-[11px] font-black text-zinc-600 uppercase tracking-widest z-10 select-none">Relative Strength (RS vs SPY)</span>
               </div>
            </div>
          </div>
          
          <div className="absolute bottom-24 left-4 z-10 flex gap-4 text-[11px] font-bold text-zinc-500 uppercase tracking-tighter opacity-60">
            <span className="flex items-center gap-1"><div className="w-2 h-0.5 bg-cyan-500" /> 10 EMA</span>
            <span className="flex items-center gap-1"><div className="w-2 h-0.5 bg-green-600" /> 21 EMA</span>
            <span className="flex items-center gap-1"><div className="w-2 h-0.5 bg-rose-600" /> 50 SMA</span>
          </div>
        </div>

        {/* Row 2: The 7-Metric Grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
          {[
            { 
              label: 'ATR (20)', 
              value: `$${fmt(etf.atr)}`, 
              sub: `${fmt(etf.atr_pct)}% of price`,
              color: 'text-zinc-200'
            },
            { 
              label: 'Vol vs 50d Avg', 
              value: `${fmt(etf.vol_vs_avg, 0)}%`, 
              sub: `${(etf.volume_current/1000000).toFixed(1)}M / ${(etf.volume_avg/1000000).toFixed(1)}M`,
              color: (etf.vol_vs_avg || 0) > 100 ? 'text-emerald-500' : 'text-rose-500'
            },
            { 
              label: 'Dist from 10-EMA', 
              value: fmtPct(etf.dist_10ema), 
              sub: `EMA: $${fmt(etf.ema_10)}`,
              color: (etf.dist_10ema || 0) >= 0 ? 'text-emerald-500' : 'text-rose-500'
            },
            { 
              label: 'Dist from 21-EMA', 
              value: fmtPct(etf.dist_21ema), 
              sub: `EMA: $${fmt(etf.ema_21)}`,
              color: (etf.dist_21ema || 0) >= 0 ? 'text-emerald-500' : 'text-rose-500'
            },
            { 
              label: 'Dist from 50-SMA', 
              value: fmtPct(etf.dist_50sma), 
              sub: `SMA: $${fmt(etf.sma_50)}`,
              color: (etf.dist_50sma || 0) >= 0 ? 'text-emerald-500' : 'text-rose-500'
            },
            { 
              label: 'RS vs SPY (3M)', 
              value: fmtPct(etf.rs_change), 
              sub: `Trend: ${etf.rs_trend}`,
              color: (etf.rs_change || 0) >= 0 ? 'text-emerald-500' : 'text-rose-500'
            },
            { 
              label: '1Y Return', 
              value: fmtPct(etf.return_1y), 
              sub: 'Past 52 Weeks',
              color: (etf.return_1y || 0) >= 0 ? 'text-emerald-500' : 'text-rose-500'
            },
          ].map((m, idx) => (
            <div key={idx} className="bg-[#111113] border border-zinc-800/40 p-3 rounded-lg flex flex-col items-center justify-center text-center group/metric hover:border-emerald-500/30 transition-all cursor-default shadow-lg">
                <span className="text-[11px] text-zinc-500 font-black uppercase tracking-widest mb-1.5">{m.label}</span>
                <span className={`text-lg font-black ${m.color} leading-none mb-1`}>{m.value}</span>
                <span className="text-[11px] text-zinc-600 font-bold tracking-tight">{m.sub}</span>
            </div>
          ))}
        </div>

        {/* Row 3: 52-Week Range Full Width */}
        <div className="bg-[#111113] border border-zinc-800/40 p-4 rounded-xl flex flex-col gap-3 shadow-lg">
           <div className="flex justify-center">
             <span className="text-[11px] font-black text-zinc-500 uppercase tracking-[0.2em]">52-Week Range</span>
           </div>
           <div className="relative h-1.5 w-full bg-zinc-900 rounded-full overflow-hidden">
             <div className="h-full bg-gradient-to-r from-rose-600 via-yellow-500 to-emerald-600 opacity-80" />
             <div 
               className="absolute top-0 h-full w-1.5 bg-white shadow-[0_0_15px_rgba(255,255,255,0.8)] z-10"
               style={{ left: `${etf.range_position}%`, transform: 'translateX(-50%)' }}
             />
           </div>
           <div className="flex justify-between items-center text-[12px] font-bold">
             <span className="text-zinc-600">${fmt(etf.low_52w)}</span>
             <span className="text-zinc-100 flex items-center gap-1.5 font-black">
                ${fmt(etf.price)} <span className="text-zinc-600 font-bold">({fmtPct(etf.dist_from_high)} from high)</span>
             </span>
             <span className="text-zinc-600">${fmt(etf.high_52w)}</span>
           </div>
        </div>

        {/* Row 4: Side by Side Zones */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
           <div className="bg-emerald-500/5 border border-emerald-500/20 p-5 rounded-xl group/zone hover:bg-emerald-500/10 transition-all cursor-default">
              <div className="flex items-center justify-center gap-2 mb-3 text-emerald-500 font-black text-[13px] uppercase tracking-widest">
                <Target className="h-4 w-4" />
                Target Zone (2R-3R)
              </div>
              <div className="text-center">
                <div className="text-4xl font-black text-emerald-500 tracking-tight mb-1">
                  ${fmt(target2R)} - ${fmt(target3R)}
                </div>
                <div className="text-[13px] text-emerald-600/80 font-bold uppercase tracking-widest">
                  Potential: {(((target3R/etf.price)-1)*100).toFixed(1)}% to {(((target3R/etf.price)-1)*100).toFixed(1)}%
                </div>
              </div>
           </div>

           <div className="bg-rose-500/5 border border-rose-500/20 p-5 rounded-xl group/zone hover:bg-rose-500/10 transition-all cursor-default">
              <div className="flex items-center justify-center gap-2 mb-3 text-rose-500 font-black text-[13px] uppercase tracking-widest">
                <ShieldAlert className="h-4 w-4" />
                Stop Zone (1R-2R)
              </div>
              <div className="text-center">
                <div className="text-4xl font-black text-rose-500 tracking-tight mb-1">
                  ${fmt(stop2R)} - ${fmt(stop1R)}
                </div>
                <div className="text-[13px] text-rose-600/80 font-bold uppercase tracking-widest">
                  Max Risk: {(((stop2R/etf.price)-1)*100).toFixed(1)}%
                </div>
              </div>
           </div>
        </div>
      </div>
    </div>
  );
};

export default ETFTradeCard;
